import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SignatureCapture } from '@/components/documents/SignatureCapture';
import { generateDocumentPdf } from '@/lib/pdfGeneration';
import { toast } from 'sonner';
import { Loader2, FileText, PenTool, BookOpen, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { CompletedDocumentViewer } from './CompletedDocumentViewer';
import { sanitizeDocumentContent } from '@/lib/sanitize';

interface JourneyCodeOfConductStepProps {
  stepId: string;
  userId: string;
  documentTitle: string;
  documentType: string;
  documentCategory: string;
  templateName: string;
  isCompleted: boolean;
  onComplete: () => void;
  completedDocumentId?: string;
  introText?: string;
}

interface UnaData {
  id: string;
  name: string;
  treasury_secretary_id: string;
}

interface ProfileData {
  full_name: string;
}

export function JourneyCodeOfConductStep({
  stepId,
  userId,
  documentTitle,
  documentType,
  documentCategory,
  templateName,
  isCompleted,
  onComplete,
  completedDocumentId,
  introText,
}: JourneyCodeOfConductStepProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unaData, setUnaData] = useState<UnaData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [templateContent, setTemplateContent] = useState<string>('');
  const [showSignature, setShowSignature] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<'preview' | 'signing' | 'filing' | 'complete'>('preview');

  // Fetch UNA data, profile, and template
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user's UNA with primary_officer_role
        const { data: una, error: unaError } = await supabase
          .from('unas')
          .select('id, name, treasury_secretary_id, primary_officer_role')
          .eq('treasury_secretary_id', userId)
          .maybeSingle();

        if (unaError) throw unaError;
        if (!una) {
          toast.error('UNA not found. Please complete previous steps first.');
          return;
        }
        setUnaData(una);

        // Fetch user profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;
        setProfileData(profile);

        // Fetch document template
        const { data: template, error: templateError } = await supabase
          .from('document_templates')
          .select('template_content')
          .eq('template_name', templateName)
          .eq('is_active', true)
          .single();

        if (templateError) throw templateError;
        setTemplateContent(template.template_content);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load step data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, templateName]);

  // Get the user's role from UNA data
  const userRole = (unaData as any)?.primary_officer_role || 'Secretary Treasurer';
  const roleSlug = userRole.toLowerCase().replace(/\s+/g, '_');

  // Generate document HTML with merge fields
  const getPopulatedHtml = (): string => {
    if (!templateContent || !unaData || !profileData) return '';

    let html = templateContent;
    html = html.replace(/\{\{una_name\}\}/g, unaData.name || '');
    html = html.replace(/\{\{signer_name\}\}/g, profileData.full_name || '');
    html = html.replace(/\{\{signer_role\}\}/g, userRole);
    html = html.replace(/\{\{signature_date\}\}/g, format(new Date(), 'MMMM d, yyyy'));

    return html;
  };

  // Wrap HTML in full ecclesiastical document styling for PDF generation
  const wrapInEcclesiasticalStyles = (bodyHtml: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      background: white;
      margin: 0;
      padding: 0;
    }
    
    h1 { 
      font-size: 18pt; 
      font-weight: bold; 
      margin: 0 0 20pt 0;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    
    h2 { 
      font-size: 13pt; 
      font-weight: bold; 
      margin: 24pt 0 10pt 0;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    
    h3 { 
      font-size: 12pt; 
      font-weight: bold; 
      font-style: italic;
      margin: 18pt 0 8pt 0;
      color: #333;
    }
    
    p { 
      margin: 0 0 14pt 0;
      font-size: 11pt;
      text-align: justify;
    }
    
    li { margin: 8pt 0; }
    ul, ol { margin: 14pt 0; padding-left: 28pt; }
    
    .signature-section {
      margin-top: 40pt;
      padding-top: 24pt;
      border-top: 1px solid #ccc;
    }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
  };

  // Handle signature capture
  const handleSignature = async (signatureData: string) => {
    if (!unaData || !profileData) return;

    setProcessing(true);
    setCurrentStep('signing');

    try {
      // 1. Embed signature into HTML
      const populatedHtml = getPopulatedHtml();
      const signedHtml = embedSignatureInHtml(populatedHtml, signatureData);
      const styledSignedHtml = wrapInEcclesiasticalStyles(signedHtml);

      // 2. Create generated_documents record first (before PDF)
      const { data: docRecord, error: docError } = await supabase
        .from('generated_documents')
      .insert({
        una_id: unaData.id,
        document_name: documentTitle,
        document_type: documentType,
        is_custom: true,
        custom_content: styledSignedHtml,
        status: 'finalized',
        created_by: userId,
        finalized_by: userId,
        version: 1,
        merge_data: {
            una_name: unaData.name,
            signer_name: profileData.full_name,
            signature_date: format(new Date(), 'yyyy-MM-dd'),
          },
          finalized_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (docError) throw docError;

      // 3. Generate and upload PDF
      setCurrentStep('filing');
      try {
        const pdfResult = await generateDocumentPdf(docRecord.id, documentTitle);

        // Update document with file URL
        await supabase
          .from('generated_documents')
          .update({
            file_url: pdfResult.file_url,
            file_path: pdfResult.file_path,
            file_size: pdfResult.file_size,
          })
          .eq('id', docRecord.id);
      } catch (pdfError) {
        console.warn('PDF generation failed, continuing without PDF:', pdfError);
        // Continue even if PDF fails - the HTML is stored
      }

      // 4. File to governance_documents with expiration date (1 year from now)
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      
      const { data: govDoc, error: govError } = await supabase
        .from('governance_documents')
        .insert({
          una_id: unaData.id,
          document_type: documentType,
          document_category: documentCategory,
          document_title: documentTitle,
          original_file_url: docRecord.file_url || '',
          generated_from_task_id: null,
          effective_date: new Date().toISOString().split('T')[0],
          expiration_date: oneYearFromNow.toISOString().split('T')[0],
          access_level: 'all_members',
          created_by: userId,
          version: 1,
          is_current_version: true,
        })
        .select('id')
        .single();

      if (govError) throw govError;

      // 4b. Create annual renewal task (due 30 days before expiration)
      const renewalDueDate = new Date(oneYearFromNow);
      renewalDueDate.setDate(renewalDueDate.getDate() - 30);
      
      // Idempotency guard: skip if an open (non-completed) renewal task already exists for this UNA
      const { data: existingRenewal } = await supabase
        .from('custom_tasks')
        .select('id')
        .eq('una_id', unaData.id)
        .eq('task_title', 'Annual Personal Attestation Renewal')
        .neq('status', 'completed')
        .limit(1)
        .maybeSingle();

      if (!existingRenewal) {
        await supabase.from('custom_tasks').insert({
          una_id: unaData.id,
          user_id: userId,
          task_title: 'Annual Personal Attestation Renewal',
          task_description: `Your Personal Attestation (${documentTitle}) expires on ${format(oneYearFromNow, 'MMMM d, yyyy')}. Renew your commitment by re-signing this foundational document to maintain your ecclesiastical standing.`,
          category_type: 'governance',
          status: 'not_started',
          importance: 'high',
          urgency: 'medium',
          due_date: renewalDueDate.toISOString(),
          recurring_config: {
            enabled: true,
            pattern: 'yearly',
            interval: 1,
            frequency_label: 'Annually',
            governance_document_id: govDoc.id,
          },
        });
      }

      // 5. Create journal entry
      const { error: journalError } = await supabase
        .from('una_journal_records' as any)
        .insert({
          una_id: unaData.id,
          record_type: 'General Record',
          record_title: `${documentTitle} Acknowledged`,
          record_date: new Date().toISOString().split('T')[0],
          activity_description: `${userRole} ${profileData.full_name} acknowledged and signed the ${documentTitle}, affirming ecclesiastical principles of stewardship for ${unaData.name}.`,
          activity_category: 'Governance',
          generated_document_id: docRecord.id,
          authorized_by: profileData.full_name,
          created_by: userId,
        } as any);

      if (journalError) throw journalError;

      // 6. Mark journey step complete
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: {
            document_id: docRecord.id,
            signed_at: new Date().toISOString(),
          },
        }, { onConflict: 'user_id,step_id' });

      // 7. Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      setCurrentStep('complete');
      setShowSignature(false);
      toast.success(`${documentTitle} signed and filed successfully!`);
      onComplete();
    } catch (error) {
      console.error('Error processing signature:', error);
      toast.error('Failed to process signature. Please try again.');
      setCurrentStep('preview');
    } finally {
      setProcessing(false);
    }
  };

  // Embed signature into HTML
  const embedSignatureInHtml = (html: string, signatureBase64: string): string => {
    const signatureHtml = `
      <div style="margin-top: 10px;">
        <img src="${signatureBase64}" alt="Signature" style="max-height: 60px; max-width: 300px;" />
        <p style="font-size: 10pt; color: #666; margin-top: 4px;">
          Electronically signed on ${format(new Date(), 'MMMM d, yyyy at h:mm a')}
        </p>
      </div>
    `;

    // Replace the signature placeholder (supports dynamic role slugs)
    const signerIdPattern = new RegExp(
      `<div[^>]*class="signature-placeholder"[^>]*data-signer-id="[^"]*"[^>]*><\\/div>`,
      'i'
    );
    return html.replace(signerIdPattern, signatureHtml);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <CompletedDocumentViewer
        documentId={completedDocumentId || ''}
        documentTitle={documentTitle}
        successMessage="Your promise is saved and filed with your Association."
      />
    );
  }

  if (!unaData || !profileData) {
    return (
      <Alert variant="destructive" className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">UNA Setup Required</AlertTitle>
        <AlertDescription className="flex flex-col gap-4">
          <span className="text-amber-700">
            Your UNA hasn't been set up yet. This step requires a UNA to sign documents.
          </span>
          <Button 
            variant="outline" 
            onClick={() => navigate('/confirm-una')}
            className="w-fit"
          >
            Set Up Your UNA
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro Text */}
      {introText && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{introText}</p>
        </div>
      )}
      
      {/* Progress Indicator */}
      {processing && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <div>
            <p className="font-medium text-blue-800">
              {currentStep === 'signing' && 'Embedding your signature...'}
              {currentStep === 'filing' && 'Filing to UNA Governance...'}
            </p>
          </div>
        </div>
      )}

      {/* Document Preview */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">{documentTitle}</h3>
          </div>
          
          <div 
            className="border rounded-lg p-6 bg-white max-h-[400px] overflow-y-auto ecclesiastical-document"
            dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(getPopulatedHtml()) }}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => setShowSignature(true)}
            disabled={processing}
            size="lg"
            className="flex-1"
          >
            <PenTool className="w-4 h-4 mr-2" />
            Sign & Commit
          </Button>
        </div>

        <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
          <BookOpen className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">What happens when you sign:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Your signature will be embedded in the document</li>
              <li>The signed document will be filed to UNA Governance</li>
              <li>A journal entry will record your commitment</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignature && (
        <SignatureCapture
          role={roleSlug}
          signerName={profileData.full_name}
          onSave={handleSignature}
          onCancel={() => setShowSignature(false)}
          isLoading={processing}
        />
      )}
    </div>
  );
}
